import * as React from 'react';
import {
  List,
  Datagrid,
  FunctionField,
  useNotify,
  Confirm,
  useRefresh,
  useListContext,
  useDelete,
  useUpdate,
} from 'react-admin';
import { Box, Chip, LinearProgress, Tooltip, Typography, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import * as Sentry from '@sentry/react';
import { IconButton } from '../../components/icon-button/IconButton';
import { CardActionIcons } from '../../components/card-actions/CardActions';
import { EmptyBox } from '../../components/empty-box/EmptyBox';
import { PageHeader } from '../../components/page-header/PageHeader';
import { BackButton } from '../../components/back-button/BackButton';
import { Switch } from '../../components/switch/Switch';
import { listSurfaceSx } from '../../core/theme/listStyles';
import { cardActionButtonSx } from '../../core/theme/buttonStyles';
import { formatMoney } from '../coupons/couponTypes';
import { AccessibleLocation, useAccessibleLocations } from '../../hooks/useAccessibleLocations';
import {
  budgetUsagePercent,
  Discount,
  DiscountStatus,
  formatDiscountValue,
  formatSchedule,
  toDiscountPayload,
} from './discountTypes';

const STATUS_SX: Record<DiscountStatus, object> = {
  ACTIVE: { backgroundColor: 'var(--color-success-soft)', color: 'var(--color-success)' },
  SCHEDULED: { backgroundColor: 'var(--color-primary-soft)', color: 'var(--color-primary)' },
  ENDED: { backgroundColor: 'var(--color-bg-muted)', color: 'var(--color-text-secondary)' },
  DISABLED: { backgroundColor: 'var(--color-bg-muted)', color: 'var(--color-text-secondary)' },
  BUDGET_EXHAUSTED: { backgroundColor: 'var(--color-warning-soft)', color: 'var(--color-warning)' },
};

const StatusChip = ({ discount }: { discount: Discount }) => {
  const { t } = useTranslation();
  const chip = (
    <Chip
      size="small"
      label={t(`discounts.status.${discount.status}`)}
      sx={STATUS_SX[discount.status]}
    />
  );

  if (discount.status === 'BUDGET_EXHAUSTED' && discount.budgetExhaustedAt) {
    return (
      <Tooltip
        title={t('discounts.status.exhaustedAt', {
          date: new Date(discount.budgetExhaustedAt).toLocaleString(),
        })}
      >
        {chip}
      </Tooltip>
    );
  }
  return chip;
};

const locationCurrency = (discount: Discount, locations: AccessibleLocation[]) =>
  locations.find((l) => l.id === discount.locationId)?.currency ?? 'PLN';

const locationName = (discount: Discount, locations: AccessibleLocation[]) =>
  locations.find((l) => l.id === discount.locationId)?.name ?? discount.locationName ?? '—';

const BudgetCell = ({ discount, currency }: { discount: Discount; currency: string }) => {
  const { t } = useTranslation();

  if (discount.budget == null) {
    return (
      <Typography sx={{ fontSize: 'var(--text-sm)' }}>{t('discounts.budget.unlimited')}</Typography>
    );
  }

  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
        {formatMoney(discount.spentAmount, currency)} / {formatMoney(discount.budget, currency)}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={budgetUsagePercent(discount)}
        color={discount.status === 'BUDGET_EXHAUSTED' ? 'warning' : 'primary'}
        sx={{ mt: 'var(--space-1)', borderRadius: 'var(--radius-sm)' }}
      />
    </Box>
  );
};

const scopeLabel = (discount: Discount, t: TFunction) =>
  discount.scope === 'ALL_ITEMS'
    ? t('discounts.scope.allItems')
    : t('discounts.scope.selectedCount', { count: discount.inventoryItemIds.length });

const thresholdLabel = (discount: Discount, currency: string, t: TFunction) =>
  discount.minOrderAmount > 0
    ? t('discounts.threshold.from', { amount: formatMoney(discount.minOrderAmount, currency) })
    : '—';

const customerRuleLabel = (discount: Discount, t: TFunction) => {
  if (discount.firstOrderOnly) return t('discounts.customerRules.firstOrder');
  if (discount.perCustomerLimit != null) {
    return t('discounts.customerRules.perCustomer', { limit: discount.perCustomerLimit });
  }
  return null;
};

// Etykieta ograniczenia dla klienta przy nazwie rabatu; bez ograniczenia nic nie pokazujemy.
const CustomerRuleChip = ({ discount }: { discount: Discount }) => {
  const { t } = useTranslation();
  const label = customerRuleLabel(discount, t);
  if (!label) return null;

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        backgroundColor: 'var(--color-bg-muted)',
        color: 'var(--color-text-secondary)',
        flexShrink: 0,
      }}
    />
  );
};

const DiscountsListHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  return (
    <PageHeader
      stretchActionsOnMobile
      back={<BackButton onClick={() => navigate('/discount-types')} />}
      title={t('discountsSection.kinds.discounts.title')}
      subtitle={t('discounts.subtitle')}
      actions={
        <IconButton
          icon={<AddIcon />}
          label={t('discounts.addDiscount')}
          sx={isMobile ? cardActionButtonSx : undefined}
          onClick={() => navigate('/discounts/create')}
        />
      }
    />
  );
};

interface ListBodyProps {
  locations: AccessibleLocation[];
  onDelete: (discount: Discount) => void;
  onToggle: (discount: Discount) => void;
}

const MobileDiscountsList = ({ locations, onDelete, onToggle }: ListBodyProps) => {
  const { data, isLoading } = useListContext<Discount>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (isLoading || !data) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {data.map((discount) => {
        const currency = locationCurrency(discount, locations);
        return (
          <Box
            key={discount.id}
            sx={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              p: 'var(--space-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontWeight: 700, fontSize: 'var(--text-sm)', flex: 1, minWidth: 0 }}
              >
                {discount.name}
              </Typography>
              <CustomerRuleChip discount={discount} />
              <StatusChip discount={discount} />
              <Switch
                checked={discount.isActive}
                onChange={() => onToggle(discount)}
                inputProps={{ 'aria-label': t('discounts.fields.isActive') }}
              />
              <CardActionIcons
                actions={[
                  {
                    key: 'edit',
                    label: t('common.edit'),
                    icon: <EditIcon fontSize="small" />,
                    onClick: () => navigate(`/discounts/${discount.id}`),
                  },
                  {
                    key: 'delete',
                    label: t('common.delete'),
                    icon: <DeleteOutlineIcon fontSize="small" />,
                    destructive: true,
                    onClick: () => onDelete(discount),
                  },
                ]}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 'var(--space-4)',
                rowGap: 'var(--space-1)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <span>{formatDiscountValue(discount, currency)}</span>
              <span>{scopeLabel(discount, t)}</span>
              <span>{thresholdLabel(discount, currency, t)}</span>
              <span>{formatSchedule(discount, t)}</span>
              <span>{locationName(discount, locations)}</span>
            </Box>
            <BudgetCell discount={discount} currency={currency} />
          </Box>
        );
      })}
    </Box>
  );
};

const DesktopDiscountsList = ({ locations, onDelete, onToggle }: ListBodyProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Datagrid
      bulkActionButtons={false}
      rowClick={false}
      sx={{ '& .RaDatagrid-headerCell': { fontWeight: 700, letterSpacing: 0.2 } }}
    >
      <FunctionField
        sortable={false}
        label={t('discounts.columns.name')}
        render={(d: Discount) => (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>{d.name}</Typography>
              <CustomerRuleChip discount={d} />
            </Box>
            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
              {locationName(d, locations)}
            </Typography>
          </Box>
        )}
      />
      <FunctionField
        sortable={false}
        label={t('discounts.columns.discount')}
        render={(d: Discount) => formatDiscountValue(d, locationCurrency(d, locations))}
      />
      <FunctionField
        sortable={false}
        label={t('discounts.columns.scope')}
        render={(d: Discount) => scopeLabel(d, t)}
      />
      <FunctionField
        sortable={false}
        label={t('discounts.columns.threshold')}
        render={(d: Discount) => thresholdLabel(d, locationCurrency(d, locations), t)}
      />
      <FunctionField
        sortable={false}
        label={t('discounts.columns.schedule')}
        render={(d: Discount) => formatSchedule(d, t)}
      />
      <FunctionField
        sortable={false}
        label={t('discounts.columns.budget')}
        render={(d: Discount) => (
          <BudgetCell discount={d} currency={locationCurrency(d, locations)} />
        )}
      />
      <FunctionField
        sortable={false}
        label={t('discounts.columns.status')}
        render={(d: Discount) => <StatusChip discount={d} />}
      />
      <FunctionField
        label=""
        sortable={false}
        render={(d: Discount) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Tooltip title={t('discounts.fields.isActive')}>
              <span>
                <Switch checked={d.isActive} onChange={() => onToggle(d)} />
              </span>
            </Tooltip>
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton icon={<EditIcon />} onClick={() => navigate(`/discounts/${d.id}`)} />
              </span>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <span>
                <IconButton
                  icon={<DeleteOutlineIcon />}
                  color="var(--color-error)"
                  onClick={() => onDelete(d)}
                />
              </span>
            </Tooltip>
          </Box>
        )}
      />
    </Datagrid>
  );
};

export const DiscountsList = () => {
  const { t } = useTranslation();
  const notify = useNotify();
  const refresh = useRefresh();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { locations } = useAccessibleLocations();
  const [deleteOne] = useDelete();
  const [update] = useUpdate();
  const [toDelete, setToDelete] = React.useState<Discount | null>(null);

  // PUT wymaga calego DiscountRequest - wysylamy jego pola z odwroconym isActive. Wlaczenie
  // rabatu z wyczerpanym budzetem backend odrzuca z czytelnym komunikatem.
  const toggle = async (discount: Discount) => {
    try {
      await update(
        'discounts',
        {
          id: discount.id,
          data: toDiscountPayload({ ...discount, isActive: !discount.isActive }),
          previousData: discount,
        },
        { returnPromise: true, mutationMode: 'pessimistic' },
      );
      notify(t('discounts.notifications.updated'), { type: 'success' });
      refresh();
    } catch (error: any) {
      notify(error?.body?.error ?? error?.message, { type: 'error' });
      Sentry.captureException(error);
    }
  };

  return (
    <>
      <DiscountsListHeader />
      <List
        resource="discounts"
        actions={false}
        perPage={25}
        empty={
          <EmptyBox
            title={t('discounts.emptyBox.title')}
            description={t('discounts.emptyBox.description')}
            resource="discounts"
          />
        }
        sx={listSurfaceSx({ isMobile })}
      >
        {isMobile ? (
          <MobileDiscountsList locations={locations} onDelete={setToDelete} onToggle={toggle} />
        ) : (
          <DesktopDiscountsList locations={locations} onDelete={setToDelete} onToggle={toggle} />
        )}
      </List>

      <Confirm
        isOpen={toDelete !== null}
        title={t('discounts.dialog.confirmDeletionTitle')}
        content={t('discounts.dialog.confirmDeletionBody', { name: toDelete?.name ?? '' })}
        confirm={t('common.delete')}
        cancel={t('common.cancel')}
        onConfirm={async () => {
          if (!toDelete) return;
          try {
            await deleteOne(
              'discounts',
              { id: toDelete.id, previousData: toDelete },
              { returnPromise: true },
            );
            notify(t('discounts.notifications.deleted'), { type: 'success' });
            refresh();
          } catch (error: any) {
            notify(error?.body?.error ?? error?.message, { type: 'error' });
            Sentry.captureException(error);
          } finally {
            setToDelete(null);
          }
        }}
        onClose={() => setToDelete(null)}
      />
    </>
  );
};

export default DiscountsList;
